import React, { useState, ChangeEvent, FormEvent, useEffect } from 'react';
import { Box, Button, TextField, Paper, CircularProgress } from '@mui/material';
import ProfilePhotoUpload from './ProfilePhotoUpload';
import { logoutUser } from '../accountSlice';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../app/store/configureStore';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UserProfileFormData } from '../../../app/data/models/accounts/userProfileFormData';
import { userProfileUpdate } from './userProfileUpdate';
import { FileWithPreview } from '../../../app/data/models/mealItems/mealItemFormData';

// Interfaces for type safety
interface UserProfileState {
  firstName: string;
  lastName: string;
  email: string;
  pictureUrl: string | null;
  currentPassword: string;
  newPassword: string;
}

const UserProfile: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.account);
  const [userState, setUserState] = useState<UserProfileState>({
    firstName: '',
    lastName: '',
    email: '',
    pictureUrl: null,
    currentPassword: '',
    newPassword: '',
  });

  const { updateProfile, isUpdating } = userProfileUpdate();
  const [previewImage, setPreviewImage] = useState<string | null>(
    userState.pictureUrl
  );

  useEffect(() => {
    if (user) {
      setUserState((prev) => ({
        ...prev,
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        pictureUrl: user.pictureUrl ?? null,
      }));
      setPreviewImage(user.pictureUrl ?? null);
    }
  }, [user]);

  const [profilePhoto, setProfilePhoto] = useState<FileWithPreview | null>(
    null
  );
  const [isPictureDeleted, setIsPictureDeleted] = useState<boolean>(false);
  const handlePhotoChange = (file: File | null, previewUrl?: string) => {
    if (file && previewUrl) {
      const fileWithPreview: FileWithPreview = Object.assign(file, {
        preview: previewUrl,
      });
      setProfilePhoto(fileWithPreview);
      setPreviewImage(previewUrl);
    } else {
      setProfilePhoto(null);
      setPreviewImage(null);
    }
  };

  // Generic handler for text input changes
  const handleInputChange =
    (prop: keyof UserProfileState) =>
    (event: ChangeEvent<HTMLInputElement>) => {
      setUserState((prev) => ({
        ...prev,
        [prop]: event.target.value,
      }));
    };

  // Form submission handlers
  const handleUpdateProfile = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const formData: UserProfileFormData = {
      firstName: (event.target as HTMLFormElement).firstName.value,
      lastName: (event.target as HTMLFormElement).lastName.value,
      picture: profilePhoto,
      isPictureDeleted: isPictureDeleted,
    };

    await updateProfile(formData);
  };

  const handleUpdatePassword = () => {
    const email = user?.email;
    if (email) {
      navigate(`/account/profile/change-password?email=${email}`);
    }
  };

  const handleLogout = () => dispatch(logoutUser());

  const { t } = useTranslation();

  return (
    <Box sx={{ maxWidth: 'sm', mx: 'auto', p: 0 }}>
      <Paper
        elevation={3}
        sx={{
          p: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {/* Profile Photo Upload */}
        <ProfilePhotoUpload
          onPhotoChange={handlePhotoChange}
          existingImageUrl={previewImage}
          onPictureDeleted={setIsPictureDeleted}
        />
        {/* Profile Information */}
        <Box
          component="form"
          onSubmit={handleUpdateProfile}
          sx={{ width: '100%', mt: 2 }}
        >
          <TextField
            fullWidth
            name="firstName"
            label={t('auth.first-name')}
            value={userState.firstName}
            onChange={handleInputChange('firstName')}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            name="lastName"
            label={t('auth.last-name')}
            value={userState.lastName}
            onChange={handleInputChange('lastName')}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            name="email"
            label={t('auth.email')}
            value={userState.email}
            disabled
            sx={{ mb: 2 }}
          />
          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            sx={{ mb: 2 }}
            disabled={isUpdating}
          >
            {isUpdating ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              t('auth.update-profile')
            )}
          </Button>
        </Box>

        {/* Password Update Section */}
        <Box
          component="form"
          onSubmit={handleUpdatePassword}
          sx={{ width: '100%' }}
        >
          <Button
            type="submit"
            variant="outlined"
            color="primary"
            fullWidth
            sx={{ mb: 2 }}
            onClick={handleUpdatePassword}
          >
            {t('auth.change-password')}
          </Button>
        </Box>

        {/* Logout Button */}
        <Button
          variant="outlined"
          color="error"
          fullWidth
          onClick={handleLogout}
          sx={{ mt: 2 }}
        >
          {t('auth.logout')}
        </Button>
      </Paper>
    </Box>
  );
};

export default UserProfile;
